import { useEffect, useRef, useState } from "react";
import service from "../../services/service";
import endpoints from "../../services/endpoints";
// Same dropdown-under-input shell as AirportAutocomplete (flight) — reused
// as-is. GET /trains/station-codes/ returns { name, code, address }, so this
// mirrors AirportAutocomplete's code/city/name layout directly.
import "../airportAutocomplete/AirportAutocomplete.css";

const TrainStationAutocomplete = ({ value, onChange, placeholder = "Enter City or Station", label, excludeCode = null }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery("");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const PANEL_HEIGHT = 320;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow);
  }, [open]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);

    clearTimeout(debounceRef.current);
    if (text.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await service.get(
          endpoints.trains.stationCodes,
          { search: text, page: 1, page_size: 10 },
          { silent: true }
        );
        setResults(data.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const handleSelect = (station) => {
    onChange(station);
    setQuery("");
    setOpen(false);
  };

  // Never let this field offer the station already picked in the paired from/to field.
  const visibleResults = excludeCode ? results.filter((station) => station.code !== excludeCode) : results;
  const showResults = open && query.trim().length >= 2;
  const displayValue = open ? query : (value ? value.code : query);

  return (
    <div className="airportAutocomplete" ref={wrapperRef}>
      {label && <label>{label}</label>}
      <input
        type="text"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />

      {!open && value && (
        <div className="airportAutocomplete__subtitle">
          {value.name}{value.address ? `, ${value.address}` : ''}
        </div>
      )}

      {showResults && (
        <div className={`airportAutocomplete__panel ${openUp ? "airportAutocomplete__panel--up" : ""}`}>
          {loading && <div className="airportAutocomplete__empty">Loading stations...</div>}
          {!loading && visibleResults.length === 0 && (
            <div className="airportAutocomplete__empty">No stations found</div>
          )}
          {!loading &&
            visibleResults.map((station) => (
              <button
                type="button"
                key={station.code}
                className="airportAutocomplete__item"
                onClick={() => handleSelect(station)}
              >
                <span className="airportAutocomplete__code">{station.code}</span>
                <span className="airportAutocomplete__details">
                  <span className="airportAutocomplete__city">{station.name}</span>
                  {station.address && <span className="airportAutocomplete__name">{station.address}</span>}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default TrainStationAutocomplete;
